import type { BlotterEntryStatus } from '@/store/blotter/api/types/BlotterEntryStatus.ts';
import {
  type BlotterCashProductType,
  type BlotterSwapProductType,
  type BlotterTradeEntry,
  isCashProductType,
  isSwapProductType,
} from '@/store/blotter/api/types/BlotterTradeEntry.ts';
import type { BlotterEntry } from '@/store/blotter/types/BlotterEntry.ts';
import type { Currency, CurrencyPair } from '@/types/currency.ts';
import type { ISODate, ISODateTime } from '@/types/date.ts';
import { parseDecimal } from '@/utils/number.ts';
import type { Rate } from '@/utils/rates.ts';

export type BlotterCashEntry = BlotterEntry & {
  //
  // technical fields
  //
  status: BlotterEntryStatus;
  execTime: ISODateTime; // "exec time" or "trade date" in legacy fx, depending on the context ...
  updateTime: ISODateTime;

  // who
  venue: string; // ex : 'SG-MARKETS'
  account: string;
  portfolio: string;
  sales: string;
  dealInitiator: string | null;
  clientId: number;

  // what
  product: BlotterCashProductType | BlotterSwapProductType;
  currencyPair: CurrencyPair;
  farDate: ISODate | null;
  farRate: Rate | null;
  nearDate: ISODate | null;
  nearRate: Rate | null;
  dealtAmount: number;
  farDealtAmount: number | null;
  dealtCurrency: Currency;
  contraAmount: number | null;
  contraCurrency: Currency;
  farContraAmount: number | null;
  points: number | null;
  spot: Rate;
  spotDate: ISODate | null;
  way: 'Sell' | 'Buy' | 'Both';
  strategyReference: string | null;
};

/**
 * Converts a Trade entry coming from webapi to a blotter cash entry exploitable by the front.
 * The input is expected to be of blotter type 'Cash', will blow otherwise.
 * @param trade A trade entry coming from webapi
 * @returns a blotter cash entry exploitable by the front
 */
export const convertCashEntry = (trade: BlotterTradeEntry): BlotterCashEntry | null => {
  if (trade.blotterType !== 'Cash') {
    throw Error('dev error');
  }
  if (!(isCashProductType(trade.product) || isSwapProductType(trade.product))) {
    // this mean the blotterType and the product field of the trade does not agree
    console.error(`convertCashEntry: unsupported product type "${trade.product}" for trade id=${trade.id}`);
    return null;
  }
  return {
    clientId: trade.counterpartyBdrId,
    account: trade.accountLongName ?? trade.account,
    sales: trade.salesFullName,
    dealInitiator: trade.electronicAccount,
    execTime: trade.date,

    // unchanged
    id: trade.id,
    updateTime: trade.updateTime,
    status: trade.status,
    currencyPair: trade.currencyPair,
    farDate: trade.farDate,
    farRate: trade.farRate,
    nearDate: trade.nearDate,
    nearRate: trade.nearRate,
    dealtAmount: trade.dealtAmount,
    farDealtAmount: parseDecimal(trade.farDealtAmount),
    dealtCurrency: trade.dealtCurrency,
    contraAmount: parseDecimal(trade.contraAmount),
    farContraAmount: parseDecimal(trade.farContraAmount),
    contraCurrency: trade.contraCurrency,
    points: parseDecimal(trade.points),
    portfolio: trade.portfolio,
    product: trade.product,
    spot: parseDecimal(trade.spot),
    spotDate: trade.spotDate,
    venue: trade.venue,
    way: trade.way,
    strategyReference: trade.strategyReference,
  };
};
